import { FadeIn } from "./FadeIn";
import {
  Factory, Wrench, Flame, Layers, HardHat, Container, Hammer,
  Building2, Car, Cog, Warehouse, Briefcase, Users, Check,
} from "lucide-react";

const INDUSTRIES = [
  { icon: Factory, label: "Manufacturing" },
  { icon: Wrench, label: "Workshops" },
  { icon: Flame, label: "Fabrication" },
  { icon: Layers, label: "Powder Coating" },
  { icon: HardHat, label: "Mining Services" },
  { icon: Container, label: "Industrial Services" },
  { icon: Hammer, label: "Trades" },
  { icon: Building2, label: "Construction Services" },
  { icon: Car, label: "Automotive" },
  { icon: Cog, label: "Mechanical Businesses" },
  { icon: Warehouse, label: "Warehousing" },
  { icon: Briefcase, label: "Commercial Service Businesses" },
  { icon: Users, label: "Multi-employee trade businesses" },
];

const VALUE_POINTS = [
  "Faster quoting", "Fewer missed enquiries", "Better follow-up", "Reduced administration",
  "Less duplicated data entry", "Fewer routine customer calls", "Better workshop visibility",
  "Improved employee utilisation", "Better stock management", "Increased throughput",
  "Better management visibility", "More time for high-value commercial work",
];

export const Industries = () => (
  <section id="industries" className="bg-white border-b border-gray-200 scroll-mt-20" data-testid="industries-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">Industries</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="industries-heading">
          Built for businesses with real operational complexity.
        </h2>
      </FadeIn>
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200" data-testid="industries-grid">
        {INDUSTRIES.map((ind, i) => (
          <FadeIn key={ind.label} delay={(i % 4) * 0.05} className="bg-white">
            <div className="p-5 sm:p-6 h-full flex items-center gap-3.5 hover:bg-offwhite transition-colors duration-200">
              <ind.icon size={19} strokeWidth={1.75} className="text-forest shrink-0" />
              <span className="text-sm font-medium text-charcoal leading-snug">{ind.label}</span>
            </div>
          </FadeIn>
        ))}
        <FadeIn delay={0.15} className="bg-sage-pale col-span-2 sm:col-span-3">
          <div className="p-5 sm:p-6 h-full flex items-center">
            <p className="text-sm text-forest font-medium leading-relaxed">
              Westline Technologies is particularly suited to established businesses where customer communication,
              staff, jobs, materials and management decisions are becoming increasingly difficult to coordinate
              through manual systems.
            </p>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export const Capacity = () => (
  <section className="relative bg-charcoal overflow-hidden" data-testid="capacity-section">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-[0.16]"
      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=85')" }}
      aria-hidden="true"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/60" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-sage uppercase mb-5">Capacity</p>
        <h2 className="font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08]" data-testid="capacity-heading">
          Increase capacity, not administrative burden.
        </h2>
        <p className="mt-6 text-base md:text-lg text-zinc-300 leading-relaxed font-medium">
          Growth should not require management to personally absorb every additional enquiry, customer call, job
          update and administrative task.
        </p>
        <p className="mt-4 text-sm md:text-base text-zinc-400 leading-relaxed">
          If a business grows another 10%, management should not personally have to become 10% busier for that
          growth to happen.
        </p>
      </FadeIn>
      <FadeIn delay={0.12} className="mt-12">
        <p className="text-[11px] font-semibold tracking-[0.24em] text-zinc-500 uppercase mb-5">
          Automation can potentially create value through
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3.5 max-w-4xl" data-testid="capacity-value-grid">
          {VALUE_POINTS.map((v) => (
            <div key={v} className="flex items-center gap-3">
              <Check size={15} className="text-sage shrink-0" strokeWidth={2.5} />
              <span className="text-sm text-zinc-300">{v}</span>
            </div>
          ))}
        </div>
        <p className="mt-10 text-[12px] text-zinc-500 max-w-2xl leading-relaxed" data-testid="capacity-disclaimer">
          Outcomes vary between businesses. Westline Technologies does not promise specific financial results — we
          identify where technology is likely to produce genuine commercial value, then build for it.
        </p>
      </FadeIn>
    </div>
  </section>
);

export const CustomDevelopment = () => (
  <section className="bg-offwhite border-b border-gray-200" data-testid="custom-development-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
      <FadeIn className="lg:col-span-5">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">Custom Development</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="custom-dev-heading">
          Not another off-the-shelf system.
        </h2>
      </FadeIn>
      <FadeIn delay={0.1} className="lg:col-span-7 space-y-5 text-base md:text-lg text-zinc-600 leading-relaxed">
        <p className="font-heading font-semibold text-charcoal text-xl md:text-2xl leading-snug">
          Most businesses do not need more software.
        </p>
        <p>
          They need the systems they already use to work together, and they need technology designed around the
          operational problems that are actually costing them time.
        </p>
        <p>
          Westline Technologies combines custom development, automation, workflow design and integrations to create
          systems specific to each business.
        </p>
      </FadeIn>
    </div>
  </section>
);
